/**
 * Uploads files as attachment versions through the server-side version
 * protocol (SYD-2378): `beginAttachmentVersion` reserves the identity, the
 * browser puts the bytes straight into Storage, `commitAttachmentVersion`
 * records the version and moves the artifact pointer inside one transaction.
 * Consumed by `useAttachments`, which supplies each existing file's
 * `currentVersionId` as the commit base — the value the panel rendered when the
 * user acted, never a fresher server read, so a version the user never saw
 * cannot be silently superseded. The hook writes no Firestore document itself.
 * A commit that fails in transport is retried exactly once (commit is
 * idempotent); a commit the server answers is never retried.
 *
 * Per-file progress is a Valtio transfer in `attachmentTransfersStore`, not
 * hook state: a 1.8 GB upload ticks many times a second and only its own row
 * should re-render. The resumable branch registers `UploadTask.cancel` as the
 * transfer's cancel handle; the `uploadBytes` branch has no handle to give, so
 * its row shows progress without a Cancel. A cancelled file resolves to null
 * and contributes no version, which is not a batch failure.
 *
 * A failed file keeps its row instead of raising a toast, and the row's Retry
 * re-enters `uploadOneFile` with the same `File` and the same base version, so
 * the second attempt is the one the reader asked for rather than a fresh batch.
 * The batch still rejects, with `AttachmentUploadBatchError`, because callers
 * chain on it — but that rejection is for the log: every failure inside it is
 * already on screen where the reader can act on it. A retry runs outside the
 * batch that spawned it, so `isUploading` stays false and the panel is not
 * locked while it goes; the row it reopens is the whole state.
 *
 * The per-file size cap is checked here rather than in the panel, because this
 * is the one place every upload passes through: no caller reaches the version
 * protocol except by way of `uploadAttachments`, so one check here covers the
 * whole surface and cannot be applied twice. It runs before
 * `beginAttachmentVersion`, so a file over the cap costs no round trip and
 * reaches no callable. The 300-attachment cap is not checked here and must not
 * be: it belongs to `Attachments.handleBatchFiles`, because the server refuses
 * it only for a reservation that adds an attachment and a new version of an
 * existing file is deliberately allowed at the cap.
 *
 * An oversized file fails only itself. It is refused inside `uploadOneFile`,
 * after its row opens and as an ordinary throw, so it takes the same lane as
 * every other per-file failure — its own transfer row carrying the reason, and a
 * place in `AttachmentUploadBatchError` — while its siblings in the batch upload
 * normally. Refusing the whole batch would discard files nothing is wrong with,
 * and refusing before the row opens would be silent, since the batch error
 * raises no toast.
 *
 * The panel uploads published versions, and a published reservation is always
 * one whole object — the layout follows the kind, so begin always answers with a
 * `storagePath` here. Chunking belongs to the desktop agent's saves. A missing
 * `storagePath` means the server reserved something this uploader cannot fill,
 * and the hook says so at once rather than failing cryptically several steps
 * deeper.
 */
import { useState, useCallback, useContext } from 'react';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, uploadBytes, getMetadata, type StorageReference } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { firestore } from '../../services/startFirestore';
import { storage } from '../../services/startStorage';
import { auth } from '../../services/startAuth';
import { functions } from '../../services/firebaseFunctions';
import { deploymentEnvironment, environmentType, emulatorPorts } from '../../config';
import {
  Attachment,
  AttachmentVersion,
  StorageCustomMetadata,
  BeginAttachmentVersionRequest,
  BeginAttachmentVersionResult,
  CommitAttachmentVersionRequest,
  CommitAttachmentVersionResult
} from '#types';
import { MessageContext } from '../../providers/MessageContext';
import { attachmentTransfersActions } from '../../store/attachmentTransfersStore';
import { resolveAttachmentSizeRefusal } from './resolveAttachmentSizeRefusal';
import { AttachmentUploadBatchError } from './AttachmentUploadBatchError';

const beginAttachmentVersion = httpsCallable<BeginAttachmentVersionRequest, BeginAttachmentVersionResult>(
  functions,
  'beginAttachmentVersion'
);

const commitAttachmentVersion = httpsCallable<CommitAttachmentVersionRequest, CommitAttachmentVersionResult>(
  functions,
  'commitAttachmentVersion'
);

/**
 * Response codes that are the server's answer to a commit rather than a
 * transport failure. Retrying one of these repeats the same answer, so the
 * single retry below is reserved for the failures that lose the answer.
 */
const commitAnswerCodes = ['failed-precondition', 'permission-denied', 'not-found', 'invalid-argument'];

/**
 * @interface ExistingFile
 * @description Maps an original filename to the attachment it should version,
 * carrying the version the artifact named when the panel rendered.
 */
interface ExistingFile {
  /** Original filename */
  name: string;
  /** Unique file identifier */
  fileUid: string;
  /**
   * The entry's `currentVersionId` as the UI showed it, or null when the entry
   * names no version. Sent verbatim as the commit's `baseVersionId`, so a user
   * looking at a stale panel is warned instead of superseding a version they
   * never saw.
   */
  currentVersionId: string | null;
  /**
   * The exact `File` this entry claims, when the caller bound the two itself
   * rather than letting the names decide — a drop onto a file row. Tested
   * before `name`, because two rows may legitimately carry the same name and
   * only the row the pointer was over may take the version.
   */
  matchesFile?: File;
  /**
   * The name to reserve the version under, when it must not be the dropped
   * file's. An entry predating SYD-2457 carries no top-level `fileName`, so
   * `commitAttachmentVersion`'s `namesTheAttachment` would make the dropped
   * name authoritative and rename the row for good.
   */
  uploadFileName?: string;
}

/**
 * @interface UploadedAttachmentVersion
 * @description One committed version, as `uploadAttachments` reports it.
 * `isCurrent` is the server's compare-and-set verdict: only a version that won
 * the pointer may be recorded as an entry's `currentVersionId`.
 */
export interface UploadedAttachmentVersion {
  /** Unique file identifier of the attachment the version belongs to. */
  fileUid: string;
  /** Identifier of the committed version. */
  versionId: string;
  /** The committed version's record, as the commit result described it. */
  versionData: AttachmentVersion;
  /** Whether the artifact's `currentVersionId` names this version now. */
  isCurrent: boolean;
}

/**
 * @interface AttachmentUploadResult
 * @description Defines the return type of the useAttachmentUpload hook.
 */
export interface AttachmentUploadResult {
  /** Indicates if an upload operation is currently in progress. */
  isUploading: boolean;
  /**
   * Function to initiate the upload of one or more files.
   * @param files - An array of File objects to upload.
   * @param artifactPath - The Firestore path to the document (e.g., form version) to associate the attachments with.
   * @param artifactType - The type of artifact ('form' or 'entity'). Defaults to 'form'.
   * @param artifactId - Optional specific ID of the artifact (e.g., form ID).
   * @param existingFiles - Optional array mapping filenames to the attachment and base version they should extend.
   * @param emailRecordId - Optional email record ID to populate in the AttachmentVersion object.
   * @returns A promise that resolves with one entry per successfully committed version; a cancelled file contributes none.
   * @throws AttachmentUploadBatchError if any file in the batch failed; each failure is already on its own row with a Retry.
   */
  uploadAttachments: (
    files: File[],
    artifactPath: string,
    artifactType?: 'form' | 'entity',
    artifactId?: string,
    existingFiles?: ExistingFile[],
    emailRecordId?: string
  ) => Promise<UploadedAttachmentVersion[]>;
  /**
   * Function to fetch the metadata for a specific attachment.
   * @param fileId - The fileUid of the attachment to fetch.
   * @returns A promise that resolves with the Attachment data or null if not found.
   */
  getFileMetadata: (fileId: string) => Promise<Attachment | null>;
}

/**
 * @function resolveContentType
 * @description Resolves the MIME type of a file, falling back to its extension
 * when the browser reports none (Windows Chrome reports an empty type for CSV).
 * @param {File} file - The file being uploaded.
 * @returns {string} The MIME type to store the object under.
 */
const resolveContentType = (file: File): string => {
  if (file.type && file.type !== '') {
    return file.type;
  }
  const extension = file.name.split('.').pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    'csv': 'text/csv',
    'txt': 'text/plain',
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'json': 'application/json',
    'xml': 'application/xml',
    'zip': 'application/zip',
  };
  return mimeTypes[extension || ''] || 'application/octet-stream';
};

/**
 * @function isCommitAnswer
 * @description Reports whether a rejected commit carries a server response code,
 * which makes it an answer rather than a lost round trip.
 * @param {unknown} error - The rejection from the commit callable.
 * @returns {boolean} True when retrying would only repeat the same answer.
 */
const isCommitAnswer = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return false;
  }
  const code = String((error as { code: unknown }).code);
  return commitAnswerCodes.some(answer => code === answer || code === `functions/${answer}`);
};

/**
 * @function useAttachmentUpload
 * @description Custom React hook to manage uploading files as attachment versions.
 * Reserves each version with `beginAttachmentVersion`, uploads the bytes to the
 * reserved storage path, and records the version with `commitAttachmentVersion`.
 * @returns {AttachmentUploadResult} An object containing upload state and functions.
 */
/**
 * Whether a `storage/unauthorized` from a non-resumable upload is really a
 * success. The Firebase SDK retries a POST whose response never arrived —
 * `!hitServer` short-circuits ahead of the retry flag in `index.node.cjs.js` —
 * and the retry meets `allow update: if false` on the object the first attempt
 * had already created. `versionId` is a freshly minted uuid the server reserved,
 * so an object at this path can only be this client's own completed upload; a
 * matching byte count confirms it landed whole. False on any other outcome,
 * including an absent object, which is a genuine rights failure.
 */
const uploadAlreadyLanded = async (
  storageRef: StorageReference,
  expectedBytes: number,
): Promise<boolean> => {
  try {
    const stored = await getMetadata(storageRef);
    return stored.size === expectedBytes;
  } catch {
    return false;
  }
};

export const useAttachmentUpload = (): AttachmentUploadResult => {
  const [isUploading, setIsUploading] = useState(false);
  const { handleFirestoreWriteError, messageApi } = useContext(MessageContext);

  /**
   * @function getFileMetadata
   * @description Fetches the metadata document for a specific attachment from Firestore.
   * @param {string} fileId - The unique identifier (fileUid) of the attachment.
   * @returns {Promise<Attachment | null>} A promise resolving to the attachment data or null if not found or on error.
   */
  const getFileMetadata = useCallback(async (fileId: string): Promise<Attachment | null> => {
    try {
      const docRef = doc(firestore, 'attachments', fileId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data() as Attachment;
    } catch (error) {
      console.error(`Failed to fetch metadata for file ${fileId}:`, error);
      return null;
    }
  }, []);

  /**
   * @function uploadAttachments
   * @description Uploads an array of files as attachment versions through the
   * begin/commit callables. Each commit carries the base version the caller
   * held when the user acted, so a version committed against a stale base is
   * recorded without moving the pointer and reported as a concurrent upload.
   * @param {File[]} files - The files to upload.
   * @param {string} artifactPath - Firestore path of the document to link the attachments to.
   * @param {'form' | 'entity'} [artifactType='form'] - Type of the linked artifact.
   * @param {string} [artifactId] - Optional ID of the linked artifact.
   * @param {ExistingFile[]} [existingFiles=[]] - Maps filenames to the attachment and base version they extend.
   * @param {string} [emailRecordId] - Optional email record ID to populate in the AttachmentVersion object.
   * @returns {Promise<UploadedAttachmentVersion[]>} One entry per successfully committed version.
   * @throws AttachmentUploadBatchError if any file in the batch failed; each failure is already on its own row with a Retry.
   */
  const uploadAttachments = useCallback(async (
    files: File[],
    artifactPath: string,
    artifactType: 'form' | 'entity' = 'form',
    artifactId?: string,
    existingFiles: ExistingFile[] = [],
    emailRecordId?: string
  ): Promise<UploadedAttachmentVersion[]> => {
    if (!files || files.length === 0) {
      return [];
    }

    if (!artifactPath) {
      throw new Error('Missing artifact path');
    }

    setIsUploading(true);

    try {
      /**
       * One file, from reservation to commit, owning the transfer row that
       * reports it. Named rather than inline because the row's Retry calls it
       * again with the same file and the same base version. The row opens
       * before the first check, so every way this can reject leaves a failure
       * the reader can see and act on — a rejection with no row would be
       * silent, since the batch error it joins raises no toast.
       *
       * A caller that bound an entry to this exact `File` wins over the name
       * comparison, and its `uploadFileName` replaces the file's own name
       * everywhere a name is written below. Only two of those places outlive the
       * upload — the reservation, from which commit copies the version's
       * `versionFileName` and the attachment document's `name`, and the object's
       * `contentDisposition`. The object's `customMetadata.fileName` does not:
       * commit calls `setMetadata` with a fresh map and GCS replaces the whole
       * map, so it is written here only to keep the in-flight object consistent
       * with the reservation. `resolveContentType` and the CSV lane still read
       * the real file: a version's type and bytes are its own, never the row's.
       */
      const uploadOneFile = async (file: File): Promise<UploadedAttachmentVersion | null> => {

        const existingFile = existingFiles.find(f => f.matchesFile === file)
          ?? existingFiles.find(f => f.name === file.name);
        const isNewFile = !existingFile;
        const baseVersionId = existingFile?.currentVersionId ?? null;
        const uploadName = existingFile?.uploadFileName ?? file.name;

        const transferId = crypto.randomUUID();
        attachmentTransfersActions.startTransfer({
          transferId,
          fileUid: existingFile?.fileUid ?? null,
          fileName: uploadName,
          kind: 'upload',
          bytesTransferred: 0,
          totalBytes: file.size,
        });

        try {
          const sizeRefusal = resolveAttachmentSizeRefusal(file);
          if (sizeRefusal) {
            throw new Error(sizeRefusal);
          }

          if (existingFile && !existingFile.fileUid) {
            throw new Error('This file has no id, so there is nothing to add a version to.');
          }

          const timestamp = Date.now();
          const contentType = resolveContentType(file);

          const currentUser = auth.currentUser;
          if (!currentUser) {
            throw new Error('Authentication required to upload attachments.');
          }

          try {
            await currentUser.getIdToken();
          } catch {
            throw new Error('Authentication required to upload attachments.');
          }

          const beginResponse = await beginAttachmentVersion({
            ...(existingFile ? { fileUid: existingFile.fileUid } : {}),
            artifactPath,
            fileName: uploadName,
            contentType,
            sizeBytes: file.size,
            kind: 'published',
            ...(emailRecordId ? { emailOriginRecordId: emailRecordId } : {})
          });

          const { fileUid, versionId, storagePath } = beginResponse.data;

          if (!storagePath) {
            throw new Error(
              `beginAttachmentVersion returned no storagePath for version ${versionId}; a published reservation must name one whole object`,
            );
          }

          const storageRef = ref(storage, storagePath);

          const customMetadata: StorageCustomMetadata = {
            metaCreated: timestamp.toString(),
            fileName: uploadName,
            versionId,
            status: 'uploading',
            artifactPath,
            fileUid,
            artifactType,
            artifactId: artifactId || '',
            metaLastModified: timestamp.toString()
          };

          const metadata = {
            contentType,
            contentDisposition: `attachment; filename="${uploadName}"`,
            customMetadata: customMetadata as { [key: string]: string }
          };

          const ext = String(file.name.split('.').pop() || '').toLowerCase();
          const useNonResumableUpload = ext === 'csv' || contentType === 'text/csv';

          if (useNonResumableUpload) {
            attachmentTransfersActions.denyCancel(transferId);

            const isLocalEmulator = environmentType === 'development' && deploymentEnvironment === 'local';
            if (isLocalEmulator) {
              const storageBucket = storage.app?.options?.storageBucket;
              if (!storageBucket) {
                throw new Error('Missing Firebase Storage bucket configuration.');
              }

              const token = await currentUser.getIdToken();
              const uploadUrl = `http://localhost:${emulatorPorts.storage}/v0/b/${storageBucket}/o?name=${encodeURIComponent(storagePath)}&uploadType=media`;

              const response = await fetch(uploadUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': contentType,
                  'Authorization': `Firebase ${token}`,
                },
                body: file,
              });

              if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                throw new Error(`Upload failed (${response.status}): ${errorText.slice(0, 200)}`);
              }
            } else {
              try {
                await uploadBytes(storageRef, file, metadata);
              } catch (error) {
                const code = (error as { code?: string } | null)?.code;
                if (code !== 'storage/unauthorized'
                  || !(await uploadAlreadyLanded(storageRef, file.size))) {
                  throw error;
                }
              }
            }
          } else {
            const uploadTask = uploadBytesResumable(storageRef, file, metadata);
            attachmentTransfersActions.allowCancel(transferId, () => uploadTask.cancel());

            const wasCancelled = await new Promise<boolean>((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  attachmentTransfersActions.reportBytes(
                    transferId,
                    snapshot.bytesTransferred,
                    snapshot.totalBytes,
                  );
                },
                (error) => {
                  if (error.code === 'storage/canceled') {
                    resolve(true);
                    return;
                  }
                  console.error(`Upload failed for ${uploadName}:`, error.code, error.message);
                  reject(new Error(error.message || error.code));
                },
                () => {
                  resolve(false);
                }
              );
            });

            if (wasCancelled) {
              attachmentTransfersActions.endTransfer(transferId);
              return null;
            }
          }

          attachmentTransfersActions.reportBytes(transferId, file.size, file.size);

          const commitRequest: CommitAttachmentVersionRequest = {
            fileUid,
            versionId,
            artifactPath,
            baseVersionId
          };

          const commitResponse = await commitAttachmentVersion(commitRequest).catch((error: unknown) => {
            if (isCommitAnswer(error)) {
              throw error;
            }
            console.warn(`Commit did not answer for ${uploadName}; retrying once.`, error);
            return commitAttachmentVersion(commitRequest);
          });

          const commit = commitResponse.data;

          if (!commit.isCurrent) {
            const winner = commit.currentVersionId
              ? 'another upload finished first and stays current'
              : 'the attachment no longer names a current version';
            messageApi.warning(
              `"${uploadName}" was saved as version ${commit.versionNumber}, but ${winner}. Open the file's version history to compare, then upload again to make your copy current.`
            );
          }

          const versionData: AttachmentVersion = {
            versionNumber: commit.versionNumber,
            versionFileName: uploadName,
            storageUrl: commit.storageUrl,
            storagePath: commit.storagePath,
            sizeBytes: commit.sizeBytes,
            type: contentType,
            ...(commit.md5Hash !== null && { md5Hash: commit.md5Hash }),
            kind: 'published',
            meta: {
              created: Timestamp.fromMillis(timestamp),
              lastModified: Timestamp.fromMillis(timestamp),
            },
            ...(emailRecordId && { emailOriginRecordId: emailRecordId })
          };

          attachmentTransfersActions.endTransfer(transferId);

          return {
            fileUid,
            versionId,
            versionData,
            isCurrent: commit.isCurrent,
          };
        } catch (error) {
          const normalizedError = error instanceof Error ? error : new Error(String(error));
          const errorMessage = isNewFile
            ? `Failed to create new file ${uploadName}: ${normalizedError.message}`
            : `Failed to add a version to ${uploadName}: ${normalizedError.message}`;
          console.error(`[${uploadName}] Upload failed:`, errorMessage);

          attachmentTransfersActions.failTransfer(
            transferId,
            normalizedError.message,
            () => {
              void uploadOneFile(file)
                .then((uploaded) => {
                  if (uploaded) {
                    messageApi.success(`Uploaded "${uploadName}"`);
                  }
                })
                .catch(() => undefined);
            },
          );

          throw new Error(errorMessage);
        }
      };

      const settledResults = await Promise.allSettled(files.map(file => uploadOneFile(file)));

      const successfulUploadData: UploadedAttachmentVersion[] = [];
      const uploadErrors: Error[] = [];

      settledResults.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          successfulUploadData.push(result.value);
        } else if (result.status === 'rejected') {
          console.error('Upload failed for one file:', result.reason);
          uploadErrors.push(result.reason instanceof Error ? result.reason : new Error(String(result.reason)));
        }
      });

      if (uploadErrors.length > 0) {
        throw new AttachmentUploadBatchError(
          uploadErrors.map(err => err.message || String(err)),
          files.length,
        );
      }

      return successfulUploadData;
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      const shownOnItsOwnRow = normalizedError instanceof AttachmentUploadBatchError;
      if (!shownOnItsOwnRow && !(normalizedError as any).__firestoreHandled) {
        handleFirestoreWriteError(normalizedError, '[useAttachmentUpload] uploadAttachments', {
          userMessage: 'Failed to upload files.',
        });
      }
      console.error('Failed to upload files:', normalizedError);
      throw normalizedError;
    } finally {
      setIsUploading(false);
    }
  }, [handleFirestoreWriteError, messageApi]);

  return {
    isUploading,
    uploadAttachments,
    getFileMetadata
  };
};
